import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Skeleton
} from '@mui/material';
// import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChatIcon from '@mui/icons-material/Chat';
import TokenIcon from '@mui/icons-material/Token';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { auth } from '../firebase';

const API_URL = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

// Estilos de tarjetas (patrón Dashboard)
const getCardStyle = (gradient, shadowColor) => ({
  p: 2.5,
  borderRadius: 4,
  background: '#fff',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: gradient
  },
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 20px 40px rgba(0, 0, 0, 0.1), 0 8px 16px ${shadowColor}`
  }
});

const getIconBoxStyle = (gradient, shadowColor) => ({
  width: 48,
  height: 48,
  borderRadius: 3,
  background: gradient,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: `0 8px 20px ${shadowColor}`,
  flexShrink: 0,
  '& svg': { fontSize: 24 }
});

const getPlanColor = (planName) => {
  const colors = {
    'Free': { gradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)', shadow: 'rgba(100, 116, 139, 0.4)' },
    'Basico': { gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', shadow: 'rgba(59, 130, 246, 0.4)' },
    'Pro': { gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', shadow: 'rgba(139, 92, 246, 0.4)' },
    'Enterprise': { gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', shadow: 'rgba(245, 158, 11, 0.4)' }
  };
  return colors[planName] || { gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', shadow: 'rgba(99, 102, 241, 0.4)' };
};

const Suscripcion = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [chatbots, setChatbots] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [loadingChatbots, setLoadingChatbots] = useState(true);

  // Histórico de consumo
  const [historicoData, setHistoricoData] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [tokensEmpresaMes, setTokensEmpresaMes] = useState(0);

  // Dialog para cambiar plan
  const [planDialog, setPlanDialog] = useState({ open: false, chatbot: null });
  const [changingPlan, setChangingPlan] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        await loadAllData(user.uid);
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const loadAllData = async (uid) => {
    setLoading(true);
    setError(null);
    try {
      // Cargar datos del usuario
      const userRes = await fetch(`${API_URL}/users/by_uid/${uid}`);
      if (!userRes.ok) throw new Error('Error al cargar datos del usuario');
      const userData = await userRes.json();
      setUserData(userData);
      if (userData.id) localStorage.setItem('user_id', userData.id);

      // Cargar planes disponibles
      const planesRes = await fetch(`${API_URL}/plans/`);
      if (planesRes.ok) {
        const planesData = await planesRes.json();
        setPlanes(planesData.filter(p => p.activo));
      }

      // Cargar chatbots de la empresa con sus planes
      if (userData?.empresa?.id) {
        await loadChatbots(userData.empresa.id);
        loadHistorico();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadChatbots = async (empresaId) => {
    setLoadingChatbots(true);
    try {
      const res = await fetch(`${API_URL}/empresas/${empresaId}/chatbots-con-uso`);
      if (res.ok) {
        const data = await res.json();
        setChatbots(data.chatbots || []);
      } else {
        // Fallback: cargar chatbots básicos desde empresa
        if (userData?.empresa?.chatbots) {
          setChatbots(userData.empresa.chatbots);
        }
      }
    } catch (err) {
      console.error('Error cargando chatbots:', err);
      // Fallback
      if (userData?.empresa?.chatbots) {
        setChatbots(userData.empresa.chatbots);
      }
    } finally {
      setLoadingChatbots(false);
    }
  };

  const loadHistorico = async () => {
    setLoadingHistorico(true);
    try {
      const userId = localStorage.getItem('user_id');
      if (!userId) return;
      const año = new Date().getFullYear();
      const res = await fetch(`${API_URL}/consumo-tokens/mensual/?año=${año}`, {
        headers: { 'X-User-Id': String(userId) }
      });
      if (res.ok) {
        const data = await res.json();
        const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        const mesActual = new Date().getMonth() + 1;
        const datosMes = (data.datos_mensuales || []).find(m => m.mes === mesActual);
        setTokensEmpresaMes(datosMes?.total_tokens || 0);
        const chartData = (data.datos_mensuales || []).map(m => ({
          mes: meses[m.mes - 1] || `M${m.mes}`,
          tokens: m.total_tokens || 0,
          conversaciones: m.conversaciones || 0
        }));
        setHistoricoData(chartData);
      }
    } catch (err) {
      console.error('Error cargando histórico:', err);
    } finally {
      setLoadingHistorico(false);
    }
  };

  const handleRefresh = () => {
    if (auth.currentUser?.uid) {
      loadAllData(auth.currentUser.uid);
    }
  };

  const openChangePlanDialog = (chatbot) => {
    setPlanDialog({ open: true, chatbot });
  };

  const handleChangePlan = async (newPlanId) => {
    if (!planDialog.chatbot) return;

    setChangingPlan(true);
    try {
      const res = await fetch(
        `${API_URL}/chatbots/${planDialog.chatbot.id}/plan?plan_id=${newPlanId}&user_id=${localStorage.getItem('user_id')}`,
        { method: 'PUT' }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Error al cambiar plan');
      }

      // Recargar datos
      await loadAllData(auth.currentUser?.uid);
      setPlanDialog({ open: false, chatbot: null });
    } catch (err) {
      setError(err.message);
    } finally {
      setChangingPlan(false);
    }
  };

  // Calcular totales
  const totalMensual = chatbots.reduce((sum, cb) => sum + (cb.plan?.precio_mensual || 0), 0);
  const totalChatbots = chatbots.length;
  const chatbotsConPlan = chatbots.filter(cb => cb.plan).length;

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num?.toString() || '0';
  };

  // Componente de tarjeta de chatbot
  const ChatbotCard = ({ chatbot }) => {
    const plan = chatbot.plan;
    const planColor = plan ? getPlanColor(plan.name) : getPlanColor('Free');

    // Calcular uso (si está disponible)
    const convUsadas = chatbot.conversaciones_usadas || 0;
    const convLimite = plan?.num_conversaciones || 100;
    const convPorcentaje = Math.min((convUsadas / convLimite) * 100, 100);

    const tokensUsados = chatbot.tokens_usados || 0;
    const tokensLimite = plan?.num_tokens || 100000;
    const tokensPorcentaje = Math.min((tokensUsados / tokensLimite) * 100, 100);

    const getProgressColor = (pct) => {
      if (pct >= 90) return '#ef4444';
      if (pct >= 75) return '#f59e0b';
      return '#10b981';
    };

    return (
      <Box sx={getCardStyle(planColor.gradient, planColor.shadow)}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box sx={getIconBoxStyle(
            chatbot.flow_type === 'intelligent'
              ? 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)'
              : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            chatbot.flow_type === 'intelligent'
              ? 'rgba(168, 85, 247, 0.4)'
              : 'rgba(99, 102, 241, 0.4)'
          )}>
            <SmartToyIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: '#1e293b', fontWeight: 700, fontSize: 16 }}>
              {chatbot.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip
                label={chatbot.flow_type === 'intelligent' ? 'IA' : 'Tradicional'}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 600,
                  bgcolor: chatbot.flow_type === 'intelligent' ? 'rgba(168, 85, 247, 0.15)' : 'rgba(99, 102, 241, 0.15)',
                  color: chatbot.flow_type === 'intelligent' ? '#a855f7' : '#6366f1'
                }}
              />
              {chatbot.whatsapp_number && (
                <Chip
                  icon={<WhatsAppIcon sx={{ fontSize: '12px !important', color: '#25D366 !important' }} />}
                  label={chatbot.whatsapp_number.replace('whatsapp:', '').slice(-4)}
                  size="small"
                  sx={{ height: 20, fontSize: 10, bgcolor: 'rgba(37, 211, 102, 0.1)', color: '#16a34a' }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Plan actual */}
        <Box sx={{
          p: 2,
          borderRadius: 3,
          background: plan ? planColor.gradient : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: '#fff',
          mb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography sx={{ fontSize: 11, opacity: 0.8, textTransform: 'uppercase', fontWeight: 600 }}>
                Plan Actual
              </Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
                {plan?.name || 'Sin Plan'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: 24, fontWeight: 800 }}>
                ${plan?.precio_mensual || 0}
              </Typography>
              <Typography sx={{ fontSize: 11, opacity: 0.8 }}>/mes</Typography>
            </Box>
          </Box>
        </Box>

        {/* Uso del mes */}
        {plan && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', mb: 1.5 }}>
              Uso del Mes
            </Typography>

            {/* Conversaciones */}
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ChatIcon sx={{ fontSize: 14, color: '#64748b' }} />
                  <Typography sx={{ fontSize: 12, color: '#64748b' }}>Conversaciones</Typography>
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
                  {formatNumber(convUsadas)} / {formatNumber(convLimite)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={convPorcentaje}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'rgba(0,0,0,0.08)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: getProgressColor(convPorcentaje),
                    borderRadius: 3
                  }
                }}
              />
            </Box>

            {/* Tokens */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TokenIcon sx={{ fontSize: 14, color: '#64748b' }} />
                  <Typography sx={{ fontSize: 12, color: '#64748b' }}>Tokens IA</Typography>
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
                  {formatNumber(tokensUsados)} / {formatNumber(tokensLimite)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={tokensPorcentaje}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'rgba(0,0,0,0.08)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: getProgressColor(tokensPorcentaje),
                    borderRadius: 3
                  }
                }}
              />
            </Box>
          </Box>
        )}

        {/* Botón cambiar plan */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<SwapHorizIcon />}
          onClick={() => openChangePlanDialog(chatbot)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#e2e8f0',
            color: '#64748b',
            py: 1,
            '&:hover': {
              borderColor: '#6366f1',
              color: '#6366f1',
              bgcolor: 'rgba(99, 102, 241, 0.05)'
            }
          }}
        >
          Cambiar Plan
        </Button>
      </Box>
    );
  };

  // Componente de resumen
  const SummaryCard = ({ icon, label, value, sublabel, gradient, shadowColor }) => (
    <Box sx={{
      ...getCardStyle(gradient, shadowColor),
      display: 'flex',
      alignItems: 'center',
      gap: 2
    }}>
      <Box sx={getIconBoxStyle(gradient, shadowColor)}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#1e293b' }}>
          {value}
        </Typography>
        {sublabel && (
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
            {sublabel}
          </Typography>
        )}
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f1f5f9'
      }}>
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#f1f5f9'
    }}>

      {/* Header */}
      <Box sx={{
        px: { xs: 2, sm: 4, md: 6 },
        py: 3,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ color: '#1e293b', fontWeight: 700, fontSize: { xs: 18, sm: 22 } }}>
              Mi Suscripcion
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>
              {userData?.empresa?.company || 'Mi Empresa'}
            </Typography>
          </Box>
          <Tooltip title="Actualizar">
            <IconButton
              onClick={handleRefresh}
              sx={{ color: '#64748b' }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{
        px: { xs: 2, sm: 4, md: 6 },
        py: 4,
        maxWidth: 1200,
        mx: 'auto'
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
        )}

        {/* Resumen de facturación */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{
            color: '#94a3b8',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            mb: 2
          }}>
            Resumen de Facturacion
          </Typography>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2
          }}>
            <SummaryCard
              icon={<ReceiptLongIcon />}
              label="Total Mensual"
              value={`$${totalMensual}`}
              sublabel="por todos los chatbots"
              gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
              shadowColor="rgba(16, 185, 129, 0.4)"
            />
            <SummaryCard
              icon={<SmartToyIcon />}
              label="Chatbots Activos"
              value={totalChatbots}
              sublabel={`${chatbotsConPlan} con plan asignado`}
              gradient="linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
              shadowColor="rgba(99, 102, 241, 0.4)"
            />
            <SummaryCard
              icon={<CalendarMonthIcon />}
              label="Proxima Renovacion"
              value="1 Feb"
              sublabel="2026"
              gradient="linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
              shadowColor="rgba(14, 165, 233, 0.4)"
            />
            <SummaryCard
              icon={<TrendingUpIcon />}
              label="Estado"
              value="Activo"
              sublabel="Suscripcion al dia"
              gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
              shadowColor="rgba(245, 158, 11, 0.4)"
            />
          </Box>
        </Box>

        {/* Consumo Total del Mes */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{
            color: '#94a3b8',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            mb: 2
          }}>
            Consumo Total del Mes
          </Typography>

          {(() => {
            const totalConvUsadas = chatbots.reduce((s, cb) => s + (cb.conversaciones_usadas || 0), 0);
            const totalConvLimite = chatbots.reduce((s, cb) => s + (cb.plan?.num_conversaciones || 0), 0);
            const tokensChatbots = chatbots.reduce((s, cb) => s + (cb.tokens_usados || 0), 0);
            const totalTokensLimite = chatbots.reduce((s, cb) => s + (cb.plan?.num_tokens || 0), 0);
            const tokensPanelChat = Math.max(0, tokensEmpresaMes - tokensChatbots);
            const totalTokensReal = tokensEmpresaMes > 0 ? tokensEmpresaMes : tokensChatbots;
            const convPct = totalConvLimite > 0 ? Math.min((totalConvUsadas / totalConvLimite) * 100, 100) : 0;
            const tokensPct = totalTokensLimite > 0 ? Math.min((totalTokensReal / totalTokensLimite) * 100, 100) : 0;
            const getBarColor = (pct) => pct >= 90 ? '#ef4444' : pct >= 75 ? '#f59e0b' : '#10b981';

            return (
              <Box sx={{
                p: 3,
                borderRadius: 4,
                background: '#fff',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)'
              }}>
                {/* Barras principales */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, mb: 3 }}>
                  {/* Conversaciones WhatsApp */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ChatIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                          Conversaciones WhatsApp
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                        {totalConvUsadas.toLocaleString()} / {totalConvLimite.toLocaleString()}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={convPct}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: 'rgba(0,0,0,0.06)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 5,
                          background: `linear-gradient(90deg, ${getBarColor(convPct)}, ${getBarColor(convPct)}dd)`
                        }
                      }}
                    />
                    <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.5 }}>
                      {convPct.toFixed(0)}% utilizado — {(totalConvLimite - totalConvUsadas).toLocaleString()} restantes
                    </Typography>
                  </Box>

                  {/* Tokens IA Total */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TokenIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                          Tokens IA Total
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                        {formatNumber(totalTokensReal)} / {formatNumber(totalTokensLimite)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={tokensPct}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: 'rgba(0,0,0,0.06)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 5,
                          background: `linear-gradient(90deg, ${getBarColor(tokensPct)}, ${getBarColor(tokensPct)}dd)`
                        }
                      }}
                    />
                    <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.5 }}>
                      {tokensPct.toFixed(0)}% utilizado — {formatNumber(totalTokensLimite - totalTokensReal)} restantes
                    </Typography>
                  </Box>
                </Box>

                {/* Desglose de tokens */}
                <Divider sx={{ mb: 2, borderColor: 'rgba(0,0,0,0.08)' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>
                  Desglose de Tokens
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                  {/* Chatbots */}
                  <Box sx={{
                    p: 1.5, borderRadius: 2,
                    bgcolor: 'rgba(99, 102, 241, 0.06)',
                    border: '1px solid rgba(99, 102, 241, 0.12)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <SmartToyIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
                        Chatbots WhatsApp
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#6366f1' }}>
                      {formatNumber(tokensChatbots)}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                      {chatbots.length} chatbot{chatbots.length !== 1 ? 's' : ''} activo{chatbots.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>

                  {/* Panel + NARA Chat */}
                  <Box sx={{
                    p: 1.5, borderRadius: 2,
                    bgcolor: 'rgba(16, 185, 129, 0.06)',
                    border: '1px solid rgba(16, 185, 129, 0.12)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <LocalHospitalIcon sx={{ fontSize: 16, color: '#10b981' }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
                        Panel + NARA Chat
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>
                      {formatNumber(tokensPanelChat)}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                      Expediente, transcripciones, IA
                    </Typography>
                  </Box>

                  {/* Total */}
                  <Box sx={{
                    p: 1.5, borderRadius: 2,
                    bgcolor: 'rgba(139, 92, 246, 0.06)',
                    border: '1px solid rgba(139, 92, 246, 0.12)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <PsychologyIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
                        Total Empresa
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#8b5cf6' }}>
                      {formatNumber(totalTokensReal)}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                      Todas las fuentes de IA
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </Box>

        {/* Gráfico Histórico de Consumo */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{
            color: '#94a3b8',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            mb: 2
          }}>
            Historico de Consumo ({new Date().getFullYear()})
          </Typography>

          <Box sx={{
            p: 3,
            borderRadius: 4,
            background: '#fff',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)'
          }}>
            {loadingHistorico ? (
              <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
            ) : historicoData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={historicoData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis
                    yAxisId="tokens"
                    orientation="left"
                    tick={{ fontSize: 11, fill: '#8b5cf6' }}
                    tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}
                  />
                  <YAxis
                    yAxisId="conv"
                    orientation="right"
                    tick={{ fontSize: 11, fill: '#0ea5e9' }}
                  />
                  <RechartsTooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff' }}
                    formatter={(value, name) => {
                      if (name === 'tokens') {
                        const f = value >= 1000000 ? `${(value/1000000).toFixed(2)}M` : value >= 1000 ? `${(value/1000).toFixed(1)}K` : value;
                        return [f, 'Tokens IA'];
                      }
                      return [value, 'Conversaciones'];
                    }}
                  />
                  <Legend
                    formatter={(value) => value === 'tokens' ? 'Tokens IA' : 'Conversaciones'}
                    wrapperStyle={{ fontSize: 12 }}
                  />
                  <Bar yAxisId="tokens" dataKey="tokens" fill="#8b5cf6" radius={[4, 4, 0, 0]} barSize={28} />
                  <Bar yAxisId="conv" dataKey="conversaciones" fill="#0ea5e9" radius={[4, 4, 0, 0]} barSize={28} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <TrendingUpIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                  No hay datos de consumo para este periodo
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Lista de Chatbots */}
        <Box>
          <Typography sx={{
            color: '#94a3b8',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            mb: 2
          }}>
            Mis Chatbots ({chatbots.length})
          </Typography>

          {loadingChatbots ? (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 2
            }}>
              {[1, 2, 3].map(i => (
                <Box key={i} sx={{ ...getCardStyle('linear-gradient(135deg, #64748b 0%, #475569 100%)', 'rgba(100,116,139,0.3)') }}>
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                </Box>
              ))}
            </Box>
          ) : chatbots.length > 0 ? (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 2
            }}>
              {chatbots.map(chatbot => (
                <ChatbotCard key={chatbot.id} chatbot={chatbot} />
              ))}
            </Box>
          ) : (
            <Box sx={{
              p: 5,
              textAlign: 'center',
              background: '#fff',
              borderRadius: 4,
              border: '1px solid #e2e8f0'
            }}>
              <SmartToyIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
              <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600, mb: 0.5 }}>
                No tienes chatbots
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                Contacta al administrador para crear tu primer chatbot
              </Typography>
            </Box>
          )}
        </Box>

        {/* Nota de ayuda */}
        <Box sx={{
          mt: 4,
          p: 3,
          borderRadius: 4,
          background: '#fffbeb',
          border: '1px solid rgba(245, 158, 11, 0.3)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <BusinessIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#92400e', mb: 0.5 }}>
                ¿Necesitas mas chatbots o recursos?
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#78350f', mb: 2 }}>
                Puedes agregar mas chatbots a tu empresa o actualizar los planes existentes.
                Contacta a nuestro equipo para planes personalizados.
              </Typography>
              <Button
                variant="contained"
                size="small"
                sx={{
                  bgcolor: '#f59e0b',
                  '&:hover': { bgcolor: '#d97706' },
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2
                }}
                onClick={() => window.open('mailto:ventas@nara.com', '_blank')}
              >
                Contactar ventas
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Dialog para cambiar plan */}
      <Dialog
        open={planDialog.open}
        onClose={() => !changingPlan && setPlanDialog({ open: false, chatbot: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: '#fff'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #164e63 0%, #0891b2 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SwapHorizIcon sx={{ color: '#fbbf24' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Cambiar Plan</Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
              {planDialog.chatbot?.name}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ color: '#64748b', fontSize: 13, mb: 3 }}>
            Selecciona el nuevo plan para este chatbot:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {planes.map(plan => {
              const isCurrentPlan = planDialog.chatbot?.plan?.id === plan.id;
              const planColor = getPlanColor(plan.name);

              return (
                <Box
                  key={plan.id}
                  onClick={() => !isCurrentPlan && !changingPlan && handleChangePlan(plan.id)}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: isCurrentPlan ? '2px solid #6366f1' : '1px solid #e2e8f0',
                    bgcolor: isCurrentPlan ? 'rgba(99, 102, 241, 0.05)' : '#fff',
                    cursor: isCurrentPlan ? 'default' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '&:hover': !isCurrentPlan ? {
                      borderColor: '#6366f1',
                      bgcolor: 'rgba(99, 102, 241, 0.05)'
                    } : {}
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: planColor.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CheckCircleIcon sx={{ color: '#fff', fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#1e293b' }}>
                        {plan.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                        {formatNumber(plan.num_conversaciones)} conv • {formatNumber(plan.num_tokens)} tokens
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#1e293b' }}>
                      ${plan.precio_mensual || 0}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#64748b' }}>/mes</Typography>
                  </Box>
                  {isCurrentPlan && (
                    <Chip
                      label="Actual"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: '#6366f1',
                        color: '#fff',
                        fontSize: 10,
                        height: 20
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          <Button
            onClick={() => setPlanDialog({ open: false, chatbot: null })}
            disabled={changingPlan}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: '#64748b'
            }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Suscripcion;
